import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import { toAbsoluteImageUrl } from '@/utils/imageUrls.js';
import { countTextLength, TEXT_LIMITS } from '@/utils/textLimits.js';

const normalizeBoard = (board) => {
  const rawImageUrl =
    board.thumbnailUrl ??
    board.imageUrl ??
    board.previewUrl ??
    board.backgroundImageUrl ??
    board.background?.thumbnailUrl ??
    board.background?.imageUrl ??
    '';

  return {
    ...board,
    id: Number(board.id),
    name: board.name ?? '',
    description: board.description ?? board.text ?? '',
    imageUrl: toAbsoluteImageUrl(rawImageUrl),
  };
};

const SUGGESTED_BACKGROUNDS_ENDPOINT = '/api/conductor/suggested-backgrounds';
const SUGGESTED_BACKGROUNDS_AVAILABILITY_ENDPOINT =
  '/api/conductor/suggested-backgrounds/availability';
const MIN_TITLE_LENGTH = 3;
const MIN_DESCRIPTION_LENGTH = 20;
const MIN_IMAGE_SIDE = 1000;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const ACCEPTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const MyBoardsPage = () => {
  const { t } = useTranslation('dashboard');
  const { token } = useAuth();
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewBoard, setPreviewBoard] = useState(null);
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);
  const [suggestionSubmitting, setSuggestionSubmitting] = useState(false);
  const [suggestionCheckingLimit, setSuggestionCheckingLimit] = useState(false);
  const [imageValidationPending, setImageValidationPending] = useState(false);
  const [notification, setNotification] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    image: null,
  });
  const [formErrors, setFormErrors] = useState({
    title: '',
    description: '',
    image: '',
    general: '',
  });
  const imageValidationId = useRef(0);

  useEffect(() => {
    const fetchBoards = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(apiUrl('/api/boards'), {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || t('boards.states.error'));
        }
        const data = await response.json();
        const list = Array.isArray(data.boards) ? data.boards : data;
        const normalized = (list ?? []).map(normalizeBoard);
        setBoards(normalized);
        if (normalized[0]) {
          setSelectedBoardId(normalized[0].id);
        }
      } catch (fetchError) {
        setError(fetchError.message);
        setBoards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, [t, token]);

  const handleSelectBoard = (boardId) => {
    setSelectedBoardId(boardId);
  };

  const handlePreview = (board) => {
    setPreviewBoard(board);
  };

  const resetSuggestionForm = () => {
    setForm({
      title: '',
      description: '',
      image: null,
    });
    setFormErrors({
      title: '',
      description: '',
      image: '',
      general: '',
    });
    setImageValidationPending(false);
  };

  const buildFileTypeError = (file) => {
    if (!file) {
      return '';
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_IMAGE_EXTENSIONS.includes(extension || '') || !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return t('boards.suggestForm.errors.imageType');
    }
    return '';
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);
      image.onload = () => {
        const width = image.width;
        const height = image.height;
        URL.revokeObjectURL(objectUrl);
        resolve({ width, height });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image load failed'));
      };
      image.src = objectUrl;
    });

  const validateImageFile = async (file) => {
    if (!file) {
      return t('boards.suggestForm.errors.imageRequired');
    }
    const typeError = buildFileTypeError(file);
    if (typeError) {
      return typeError;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return t('boards.suggestForm.errors.imageSize');
    }
    try {
      const { width, height } = await getImageDimensions(file);
      if (width < MIN_IMAGE_SIDE || height < MIN_IMAGE_SIDE) {
        return t('boards.suggestForm.errors.imageResolution');
      }
    } catch (loadError) {
      return t('boards.suggestForm.errors.imageLoad');
    }
    return '';
  };

  const validateTitle = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return t('boards.suggestForm.errors.titleRequired');
    }
    if (countTextLength(trimmedValue) < MIN_TITLE_LENGTH) {
      return t('boards.suggestForm.errors.titleMin', { count: MIN_TITLE_LENGTH });
    }
    if (countTextLength(trimmedValue) > TEXT_LIMITS.shortText) {
      return t('boards.suggestForm.errors.titleMax', { count: TEXT_LIMITS.shortText });
    }
    return '';
  };

  const validateDescription = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return t('boards.suggestForm.errors.descriptionRequired');
    }
    if (countTextLength(trimmedValue) < MIN_DESCRIPTION_LENGTH) {
      return t('boards.suggestForm.errors.descriptionMin', { count: MIN_DESCRIPTION_LENGTH });
    }
    if (countTextLength(trimmedValue) > TEXT_LIMITS.supportText) {
      return t('boards.suggestForm.errors.descriptionMax', { count: TEXT_LIMITS.supportText });
    }
    return '';
  };

  const handleSuggestLimitCheck = async () => {
    if (!token) {
      setNotification({
        title: t('boards.suggestNotifications.errorTitle'),
        message: t('boards.suggestNotifications.authRequired'),
      });
      return;
    }
    setSuggestionCheckingLimit(true);
    try {
      const response = await fetch(apiUrl(SUGGESTED_BACKGROUNDS_AVAILABILITY_ENDPOINT), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(t('boards.suggestNotifications.checkError'));
      }
      const canSubmit = data?.canSubmit === true;
      const limitReached = data?.canSubmit === false && data?.reason === 'DAILY_LIMIT_REACHED';
      if (limitReached) {
        setNotification({
          title: t('boards.suggestNotifications.limitTitle'),
          message: t('boards.suggestNotifications.limitMessage'),
        });
        return;
      }
      if (!canSubmit) {
        throw new Error(t('boards.suggestNotifications.checkError'));
      }
      resetSuggestionForm();
      setSuggestModalOpen(true);
    } catch (limitError) {
      setNotification({
        title: t('boards.suggestNotifications.errorTitle'),
        message: limitError.message || t('boards.suggestNotifications.checkError'),
      });
    } finally {
      setSuggestionCheckingLimit(false);
    }
  };

  const handleSuggestChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: field === 'title' ? validateTitle(value) : validateDescription(value),
      general: '',
    }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, image: file }));
    setFormErrors((prev) => ({ ...prev, image: '', general: '' }));
    if (!file) {
      setFormErrors((prev) => ({ ...prev, image: t('boards.suggestForm.errors.imageRequired') }));
      setImageValidationPending(false);
      return;
    }
    const validationId = imageValidationId.current + 1;
    imageValidationId.current = validationId;
    setImageValidationPending(true);
    const errorMessage = await validateImageFile(file);
    if (imageValidationId.current !== validationId) {
      return;
    }
    setFormErrors((prev) => ({ ...prev, image: errorMessage }));
    setImageValidationPending(false);
  };

  const handleSuggestSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      setFormErrors((prev) => ({ ...prev, general: t('boards.suggestNotifications.authRequired') }));
      return;
    }
    const titleError = validateTitle(form.title);
    const descriptionError = validateDescription(form.description);
    const imageError = await validateImageFile(form.image);
    setFormErrors((prev) => ({
      ...prev,
      title: titleError,
      description: descriptionError,
      image: imageError,
      general: '',
    }));
    if (titleError || descriptionError || imageError) {
      return;
    }
    setSuggestionSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('description', form.description.trim());
      formData.append('image', form.image);
      const response = await fetch(apiUrl(SUGGESTED_BACKGROUNDS_ENDPOINT), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setSuggestModalOpen(false);
        resetSuggestionForm();
        setNotification({
          title: t('boards.suggestNotifications.successTitle'),
          message: t('boards.suggestNotifications.successMessage'),
        });
        return;
      }
      const limitReached =
        response.status === 429 ||
        data?.error === 'DAILY_LIMIT_REACHED' ||
        data?.code === 'DAILY_LIMIT_REACHED' ||
        data?.reason === 'DAILY_LIMIT_REACHED' ||
        data?.limitReached === true;
      if (limitReached) {
        setSuggestModalOpen(false);
        setNotification({
          title: t('boards.suggestNotifications.limitTitle'),
          message: t('boards.suggestNotifications.limitMessage'),
        });
        return;
      }
      if (data?.errors) {
        const normalizeFieldError = (value) => {
          if (Array.isArray(value)) {
            return value
              .map((item) => (typeof item === 'string' ? item : item?.message))
              .filter(Boolean)
              .join(' ');
          }
          return value || '';
        };
        if (Array.isArray(data.errors)) {
          const errorMap = data.errors.reduce((acc, item) => {
            if (item?.field && item?.message) {
              acc[item.field] = acc[item.field]
                ? `${acc[item.field]} ${item.message}`
                : item.message;
            }
            return acc;
          }, {});
          setFormErrors((prev) => ({
            ...prev,
            title: errorMap.title || prev.title,
            description: errorMap.description || prev.description,
            image: errorMap.image || prev.image,
            general: errorMap.general || '',
          }));
          return;
        }
        setFormErrors((prev) => ({
          ...prev,
          title: normalizeFieldError(data.errors.title) || prev.title,
          description: normalizeFieldError(data.errors.description) || prev.description,
          image: normalizeFieldError(data.errors.image) || prev.image,
          general: normalizeFieldError(data.errors.general),
        }));
        return;
      }
      throw new Error(data?.error || t('boards.suggestNotifications.generalError'));
    } catch (submitError) {
      setFormErrors((prev) => ({
        ...prev,
        general: submitError.message || t('boards.suggestNotifications.generalError'),
      }));
    } finally {
      setSuggestionSubmitting(false);
    }
  };

  const hintLines = t('boards.suggestForm.hint').split('\n');
  const isFormValid =
    form.title.trim().length >= MIN_TITLE_LENGTH &&
    form.description.trim().length >= MIN_DESCRIPTION_LENGTH &&
    form.image &&
    !formErrors.title &&
    !formErrors.description &&
    !formErrors.image &&
    !imageValidationPending;

  return (
    <div className="page-card">
      <div className="page-card-header deck-header">
        <div>
          <p className="page-caption">{t('boards.heading.caption')}</p>
          <h1>{t('boards.heading.title')}</h1>
          <p className="page-description">{t('boards.heading.description')}</p>
        </div>
        <div className="deck-header-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={handleSuggestLimitCheck}
            disabled={suggestionCheckingLimit}
          >
            {suggestionCheckingLimit ? t('boards.actions.proposeChecking') : t('boards.actions.propose')}
          </button>
        </div>
      </div>

      {loading && <p className="page-description">{t('boards.states.loading')}</p>}
      {error && <p className="form-error">{error}</p>}

      {!loading && !error && (
        <>
          <div className="board-grid">
            {boards.map((board) => {
              const selected = board.id === selectedBoardId;
              const title = board.name || t('boards.states.emptyName', { id: board.id });
              const description = board.description || t('boards.states.emptyDescription');
              return (
                <div
                  key={board.id}
                  className={`board-card ${selected ? 'selected' : ''}`}
                >
                  <div
                    className="board-card__image"
                    style={{
                      backgroundImage: board.imageUrl ? `url(${board.imageUrl})` : undefined,
                    }}
                    onClick={() => handlePreview(board)}
                  >
                    <div className="board-card__overlay">
                      <p className="board-caption">{t('boards.cards.caption')}</p>
                      <h3>{title}</h3>
                      <p className="board-description">{description}</p>
                    </div>
                  </div>
                  <div className="board-card__footer">
                    <button
                      type="button"
                      className={selected ? 'primary-button outlined success' : 'primary-button'}
                      onClick={() => handleSelectBoard(board.id)}
                    >
                      {selected ? t('boards.cards.selected') : t('boards.cards.select')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {previewBoard && (
        <div className="modal-overlay" onClick={() => setPreviewBoard(null)}>
          <div
            className="modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="modal-image"
              style={{ backgroundImage: previewBoard.imageUrl ? `url(${previewBoard.imageUrl})` : undefined }}
            />
            <div className="modal-info">
              <h3>{previewBoard.name || t('boards.states.emptyName', { id: previewBoard.id })}</h3>
              <p>{previewBoard.description || t('boards.states.emptyDescription')}</p>
              <div className="modal-actions board-preview-actions">
                <button
                  type="button"
                  className="primary-button outlined"
                  onClick={() => setPreviewBoard(null)}
                >
                  {t('boards.cards.close')}
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => {
                    handleSelectBoard(previewBoard.id);
                    setPreviewBoard(null);
                  }}
                >
                  {t('boards.cards.selectField')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {suggestModalOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{t('boards.suggestForm.title')}</h3>
              <button
                type="button"
                className="ghost-button"
                onClick={() => setSuggestModalOpen(false)}
              >
                {t('boards.suggestForm.actions.cancel')}
              </button>
            </div>
            <p className="modal-description">{t('boards.suggestForm.subtitle')}</p>
            <form className="game-form" onSubmit={handleSuggestSubmit}>
              <label className={`form-field${formErrors.title ? ' form-field--error' : ''}`}>
                <span>{t('boards.suggestForm.fields.title.label')}</span>
                <input
                  type="text"
                  value={form.title}
                maxLength={TEXT_LIMITS.shortText}
                  onChange={handleSuggestChange('title')}
                  placeholder={t('boards.suggestForm.fields.title.placeholder')}
                  required
                />
                {formErrors.title && <p className="form-error">{formErrors.title}</p>}
              </label>

              <label className={`form-field${formErrors.description ? ' form-field--error' : ''}`}>
                <span>{t('boards.suggestForm.fields.description.label')}</span>
                <textarea
                  value={form.description}
                maxLength={TEXT_LIMITS.supportText}
                  onChange={handleSuggestChange('description')}
                  placeholder={t('boards.suggestForm.fields.description.placeholder')}
                  required
                />
                {formErrors.description && <p className="form-error">{formErrors.description}</p>}
              </label>

              <label className={`form-field${formErrors.image ? ' form-field--error' : ''}`}>
                <span>{t('boards.suggestForm.fields.image.label')}</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  onChange={handleImageChange}
                  required
                />
                {hintLines.map((line, index) => (
                  <p key={`${line}-${index}`} className="form-hint">
                    {line}
                  </p>
                ))}
                {imageValidationPending && (
                  <p className="form-hint">{t('boards.suggestForm.fields.image.checking')}</p>
                )}
                {formErrors.image && <p className="form-error">{formErrors.image}</p>}
              </label>

              {formErrors.general && <p className="form-error">{formErrors.general}</p>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="primary-button outlined"
                  onClick={() => setSuggestModalOpen(false)}
                  disabled={suggestionSubmitting}
                >
                  {t('boards.suggestForm.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={!isFormValid || suggestionSubmitting}
                >
                  {suggestionSubmitting
                    ? t('boards.suggestForm.actions.submitting')
                    : t('boards.suggestForm.actions.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {notification && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{notification.title}</h3>
            </div>
            <p className="modal-description">{notification.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => setNotification(null)}
              >
                {t('boards.suggestNotifications.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBoardsPage;
